import * as React from "react";
import InviteProfile from "./InviteProfile";

import ProfileTags from "./profile-components/ProfileTags";
import ProfileName from "./profile-components/ProfileName";
import ProfileLocation from "./profile-components/ProfileLocation";
import ProfileCompensation from "./profile-components/ProfileCompensation";
import ProfileDistance from "./profile-components/ProfileDistance";
import ProfileJobType from "./profile-components/ProfileJobType";
import ProfileBadges from "./ProfileBadges";

import AddFavoriteCandidate from "../../favorites/components/AddFavoriteCandidate";
import AddBlockCandidate from "../../favorites/components/AddBlockCandidate";

import { ListContext } from './ProfileList';

interface Profile {
  id: number;
  first_name: string;
  last_name: string;
  city: string;
  state: string;
  zip: string;
  image: string;
  url: string;
  compensation: string | number;
  job_types: Array<any>;
  skills: Array<any>;
  position_types: Array<any>;
  badges: Array<any>;
  /** Quarterly job-search check-in answer. Present only for admin viewers;
   *  the label and Bootstrap variant are computed server-side (JobSearchStatusResponse). */
  job_search_status?: string | null;
  job_search_status_label?: string | null;
  job_search_status_variant?: string | null;
}

interface Job {
  [prop: string]: any;
}

// Admin-only AI Match details for one card, built by admin_ai_match_props.
export interface AiMatch {
  rank: number;
  label: string;
  variant: string;
  tooltip?: string;
  strengths?: string;
  breakdown?: string;
  reasoning?: string;
  invited_label?: string;
  job_search_answered_on?: string;
}

interface ProfileProps {
  profile: Profile;
  job?: any;
  user_id?: number;
  authenticity_token?: string;
  distance?: number;
  favorites?: Array<number>;
  blocked?: Array<number>;
  requested?: Array<number>;
  accepted?: Array<number>;
  declined?: Array<number>;
  cancelled?: Array<number>;
  addToRequestedFunction?: (id: number) => void;
  requestingAll?: boolean;
  isBeingProcessed?: boolean;
  processingState?: 'idle' | 'pending' | 'processing' | 'completed';
  showPositionTypes?: boolean;
  isApplicant?: boolean;
  isCancelled?: boolean;
  isDeclined?: boolean;
  aiMatch?: AiMatch;
}

/**
 * Profile Component - Organized Domain Version
 * 
 * Displays a profile card with all profile information, actions, and invite functionality.
 * This component handles both individual and batch processing states for profile interactions.
 * 
 * Features:
 * - Profile display with image, name, location, compensation
 * - Job type and skill tags
 * - Favorite/block actions
 * - Invite/request functionality with batch processing support
 * - Responsive design for mobile/desktop
 */
export const Profile: React.FC<ProfileProps> = ({
  profile,
  authenticity_token = "",
  favorites = [],
  blocked = [],
  job = {},
  requested = [],
  accepted = [],
  declined = [],
  cancelled = [],
  addToRequestedFunction = undefined,
  distance = undefined,
  user_id = undefined,
  requestingAll = false,
  isBeingProcessed = false,
  processingState = 'idle',
  showPositionTypes = true,
  isApplicant = false,
  isCancelled = false,
  isDeclined = false,
  aiMatch = undefined
}) => {
  const [state, setState] = React.useState({
    blocked: false,
    loading: false,
    favorite: favorites.indexOf(profile.id) >= 0,
    requested: requested.indexOf(profile.id) >= 0,
    accepted: accepted.indexOf(profile.id) >= 0,
    declined: declined.indexOf(profile.id) >= 0
  });

  const blockedCallback = () => setState(prevState => ({ ...prevState, blocked: !prevState.blocked }));
  const job_id = job ? job.id : undefined;
  const [context, setContext] = React.useContext(ListContext);
  const contextBlocked = context?.blocked?.includes(profile.id) || false;
  const badges = profile.badges || [];
  const jobSearchBadge = profile.job_search_status_label
    ? { label: profile.job_search_status_label, className: `bg-${profile.job_search_status_variant || 'secondary'}` }
    : undefined;

  const profile_favorited = favorites.includes(profile.id);
  const profile_blocked = blocked.includes(profile.id);

  React.useEffect(() => {
    setState(prevState => ({
      ...prevState,
      requested: requested.indexOf(profile.id) >= 0,
      accepted: accepted.indexOf(profile.id) >= 0,
      declined: declined.indexOf(profile.id) >= 0
    }));
  }, [requested, accepted, declined]);

  const renderActionButtons = () => {
    // Show different states based on batch processing
    if (processingState === 'completed' || state.requested || state.accepted) {
      return (
        <button className="btn-modern btn-modern--success btn-modern--sm" disabled>
          <i className="fad fa-check"></i> {job && job.position_type == 'temp' ? "Requested" : "Invited"}
        </button>
      );
    }

    if (processingState === 'processing') {
      return (
        <button className="btn-modern btn-modern--primary btn-modern--sm" disabled>
          <i className="fad fa-spinner fa-spin"></i> Processing...
        </button>
      );
    }

    if (processingState === 'pending' || (requestingAll && !state.requested && !state.accepted)) {
      return (
        <button className="btn-modern btn-modern--sm" disabled style={{
          background: 'linear-gradient(135deg, #ffc107, #ffb300)',
          color: '#212529'
        }}>
          <i className="fad fa-clock"></i> Pending...
        </button>
      );
    }

    if (state.loading) {
      return (
        <button className="btn-modern btn-modern--primary btn-modern--sm" disabled>
          <i className="fad fa-spinner fa-spin"></i> Requesting...
        </button>
      );
    }

    // Default invite button (not part of batch processing)
    return null; // Let the InviteProfile component handle the default state
  };

  return (
    <div
      className={`field transition-profile ${state.blocked ? 'blocked' : ''} ${contextBlocked ? 'contextBlocked' : ''}`}
      key={profile.id}
      // .transition-profile caps cards at 600px for the block animation; AI
      // strengths and an opened Reasoning outgrow it at phone width. The
      // blocked rule is !important, so blocking still collapses the card.
      style={{ cursor: 'pointer', ...(aiMatch ? { maxHeight: 'none' } : {}) }}
      onClick={() => window.open(profile.url, "_blank")}
    >
      <li className="col-12 d-flex justify-content-between border-top border-start border-end py-2 pt-1 pb-3 border-radius-1 flex-wrap">
        <div className="p-3 d-none d-md-block" style={{ width: 'auto' }}>
          <a href={profile.url} target="_blank" rel="noopener noreferrer">
            <img
              src={profile.image}
              className="rounded-circle"
              alt={`${profile.first_name} ${profile.last_name}`}
              style={{
                width: '60px',
                height: '60px',
                objectFit: 'cover',
                border: '3px solid #e8ecef',
                transition: 'all 0.3s ease'
              }}
              onMouseOver={(e) => e.currentTarget.style.border = '3px solid #085ec8'}
              onMouseOut={(e) => e.currentTarget.style.border = '3px solid #e8ecef'}
            />
          </a>
        </div>
        <div className="d-flex flex-column justify-content-center ps-4 ps-md-0 pe-4 pe-md-0" style={{ flex: 1 }}>
          <div className="job-list-content">
            <div className="col-12 d-flex flex-row align-items-center" style={{ position: 'relative' }}>
              <div className="p-3 d-block d-md-none" style={{ width: 'auto' }}>
                <a href={profile.url} target="_blank" rel="noopener noreferrer">
                  <img
                    src={profile.image}
                    className="rounded-circle"
                    alt={`${profile.first_name} ${profile.last_name}`}
                    style={{
                      width: '60px',
                      height: '60px',
                      objectFit: 'cover',
                      border: '3px solid #e8ecef',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.border = '3px solid #085ec8'}
                    onMouseOut={(e) => e.currentTarget.style.border = '3px solid #e8ecef'}
                  />
                </a>
              </div>
              <ProfileName
                blockedCallback={() => blockedCallback}
                favorites={favorites}
                blocked={blocked}
                profile={profile}
                job_id={job_id}
                user_id={user_id}
                authenticity_token={authenticity_token}
                isApplicant={isApplicant}
                isCancelled={isCancelled}
                isDeclined={isDeclined}
                invite_component={
                  job.hasOwnProperty('id') && (
                    <InviteProfile
                      requested={requested}
                      accepted={accepted}
                      declined={declined}
                      profile={profile}
                      job={job}
                      authenticity_token={authenticity_token}
                      callback={addToRequestedFunction}
                      requestingAll={requestingAll}
                      processingState={processingState}
                    />
                  )
                }
              />
              <div className="profile-actions-container d-none d-md-block" style={{ position: 'absolute', right: '1rem', top: '0' }}>
                <div className="profile-actions" style={{ display: 'flex', gap: '0.5rem' }}>
                  <AddFavoriteCandidate
                    profile={profile}
                    favorited_init={profile_favorited}
                    authenticity_token={authenticity_token}
                    expandDirection="left"
                  />
                  {(job_id || user_id) && <AddBlockCandidate
                    profile={profile}
                    job_id={job_id}
                    user_id={user_id}
                    favorited_init={profile_blocked}
                    authenticity_token={authenticity_token}
                    blockedCallback={blockedCallback}
                    expandDirection="left"
                  />}
                  {job.hasOwnProperty('id') && (
                    <InviteProfile
                      requested={requested}
                      accepted={accepted}
                      declined={declined}
                      profile={profile}
                      job={job}
                      authenticity_token={authenticity_token}
                      callback={addToRequestedFunction}
                      requestingAll={requestingAll}
                      processingState={processingState}
                    />
                  )}
                </div>
              </div>
            </div>
            {(badges.length > 0 || jobSearchBadge) && (
              <div className="d-flex my-2 flex-wrap align-items-center gap-2">
                {jobSearchBadge && (
                  <span
                    className={`badge ${jobSearchBadge.className}`}
                    title={aiMatch?.job_search_answered_on || 'Quarterly job-search check-in'}
                    data-testid="job-search-status-badge"
                  >
                    {jobSearchBadge.label}
                  </span>
                )}
                {badges.length > 0 && <ProfileBadges badges={badges} />}
              </div>
            )}
            {aiMatch && (
              <div className="my-2" data-testid="ai-match">
                <div className="d-flex flex-wrap align-items-center gap-2">
                  <span className="text-muted small">#{aiMatch.rank}</span>
                  <span className={`badge bg-${aiMatch.variant}`} title={aiMatch.tooltip}>
                    <i className="bi bi-stars me-1"></i>{aiMatch.label}
                  </span>
                  {aiMatch.invited_label && (
                    <span className="badge bg-info" title="Sent by the daily automatic invite sweep">
                      <i className="bi bi-send me-1"></i>{aiMatch.invited_label}
                    </span>
                  )}
                </div>
                {aiMatch.strengths && <div className="small text-muted mt-1">{aiMatch.strengths}</div>}
                {aiMatch.breakdown && <div className="small text-muted">{aiMatch.breakdown}</div>}
                {aiMatch.reasoning && (
                  // The card opens the profile on click; reading the reasoning must not.
                  <details className="mt-1" onClick={(e) => e.stopPropagation()}>
                    <summary className="small text-muted">Reasoning</summary>
                    <div className="small">{aiMatch.reasoning}</div>
                  </details>
                )}
              </div>
            )}
          </div>
          <div className="profile-actions-container d-md-none">
            <div className="profile-actions" style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <AddFavoriteCandidate
                profile={profile}
                favorited_init={profile_favorited}
                authenticity_token={authenticity_token}
                expandDirection="left"
              />
              {(job_id || user_id) && <AddBlockCandidate
                profile={profile}
                job_id={job_id}
                user_id={user_id}
                favorited_init={profile_blocked}
                authenticity_token={authenticity_token}
                blockedCallback={blockedCallback}
                expandDirection="left"
              />}
              {job.hasOwnProperty('id') && (
                <InviteProfile
                  requested={requested}
                  accepted={accepted}
                  declined={declined}
                  profile={profile}
                  job={job}
                  authenticity_token={authenticity_token}
                  callback={addToRequestedFunction}
                  requestingAll={requestingAll}
                  processingState={processingState}
                />
              )}
            </div>
          </div>
        </div>
        <div className="col-12 d-flex my-2 flex-wrap px-4">
          {profile.job_types && profile.job_types.length > 1 &&
            profile.job_types.slice(0, 1).map((jt: string, index: number) => (
              <ProfileJobType key={`job-type-${index}`} job_type={jt} />
            ))
          }
          {profile.job_types && profile.job_types.length > 1 &&
            <>
              <span className="profile-attribute-bubble rounded px-2 py-1 me-1 mb-1 d-md-none col-12">
                <i className="fa fa-plus"></i> {profile.job_types.length - 1} More
              </span>
              <span className="profile-attribute-bubble rounded px-2 py-1 me-1 mb-1 d-none d-md-block">
                <i className="fa fa-plus"></i> {profile.job_types.length - 1} More
              </span>
            </>
          }
          {profile.job_types && profile.job_types.length < 2 &&
            profile.job_types.map((jt: string, index: number) => (
              <ProfileJobType key={`job-type-single-${index}`} job_type={jt} />
            ))}
          {profile.city && profile.state && profile.zip && (
            <ProfileLocation
              city={profile.city}
              state={profile.state}
              zip={profile.zip}
            />
          )}
          {profile.compensation && (
            <ProfileCompensation compensation={profile.compensation} />
          )}
          {distance && <ProfileDistance distance={distance} />}
        </div>
        <div className="col-12">
          <ProfileTags profile={profile} showPositionTypes={showPositionTypes} />
        </div>
      </li>
      <div className="clearfix"></div>
    </div>
  );
};

export default Profile;
